import { Entity } from '@backstage/catalog-model';
import { DiscoveryApi } from '@backstage/core-plugin-api';
import { CatalogApi } from '@backstage/plugin-catalog-react';

import {
  WatchtowerRecommendation,
  WatchtowerRecommendationsResponse,
  WatchtowerBatchResponse,
  RevisionRequest,
} from './types';

/**
 * Fetch catalog repositories with proposed ownership status
 * @deprecated This function is no longer needed. Use fetchWatchtowerRecommendations('/by-team') instead,
 * which now returns both initial proposals and revisions in a single call.
 */
export async function fetchCatalogRepositories(
  catalogApi: CatalogApi,
  groupNames: string[],
): Promise<Entity[]> {
  const { items: allRepositories } = await catalogApi.getEntities({
    filter: { kind: 'Repository' },
  });

  return allRepositories.filter((repo: Entity) => {
    const proposedOwner =
      repo.metadata.annotations?.['tomtom.com/proposed-owner'];
    const ownershipStatus =
      repo.metadata.annotations?.['tomtom.com/ownership-status'];

    if (ownershipStatus !== 'proposed' || !proposedOwner) {
      return false;
    }

    const teamName = proposedOwner.replace('group:default/', '');
    return groupNames.includes(teamName);
  });
}

/**
 * Fetch watchtower recommendations for user's teams
 */
export async function fetchWatchtowerRecommendations(
  discoveryApi: DiscoveryApi,
  supIds: string[],
): Promise<WatchtowerRecommendation[]> {
  if (supIds.length === 0) {
    return [];
  }

  try {
    const baseUrl = await discoveryApi.getBaseUrl('engineering-insights');
    const response = await fetch(
      `${baseUrl}/api/v1/repos/ownership/recommendations/by-team`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recommended_team_ids: supIds }),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch watchtower recommendations: ${response.statusText}`,
      );
    }

    const data: WatchtowerRecommendationsResponse = await response.json();
    return data.data || [];
  } catch (error) {
    return [];
  }
}

/**
 * Fetch watchtower batch recommendations for specific repositories
 * @deprecated This function is no longer needed. Use fetchWatchtowerRecommendations('/by-team') instead,
 * which now returns complete ownership data including initial proposals.
 */
export async function fetchWatchtowerBatch(
  discoveryApi: DiscoveryApi,
  repositoryNames: string[],
): Promise<WatchtowerBatchResponse> {
  if (repositoryNames.length === 0) {
    return {
      data: {},
      total_requested: 0,
      total_found: 0,
      generated_at: new Date().toISOString(),
    };
  }

  try {
    const baseUrl = await discoveryApi.getBaseUrl('engineering-insights');
    const response = await fetch(
      `${baseUrl}/api/v1/repos/ownership/recommendations/batch`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ repository_names: repositoryNames }),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch watchtower batch recommendations: ${response.statusText}`,
      );
    }

    return response.json();
  } catch (error) {
    return {
      data: {},
      total_requested: repositoryNames.length,
      total_found: 0,
      generated_at: new Date().toISOString(),
    };
  }
}

/**
 * Submit ownership revisions to watchtower
 */
export async function submitRevisions(
  discoveryApi: DiscoveryApi,
  revisions: RevisionRequest[],
): Promise<void> {
  const baseUrl = await discoveryApi.getBaseUrl('engineering-insights');

  const response = await fetch(
    `${baseUrl}/api/v1/repos/ownership/recommendations`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(revisions),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to submit revisions: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }
}

/**
 * Filter catalog repos using watchtower validation
 * Removes repos that have been revised away from user's teams
 * @deprecated This function is no longer needed. Use fetchWatchtowerRecommendations('/by-team') instead,
 * which handles filtering automatically on the backend.
 */
export function validateCatalogReposWithWatchtower(
  catalogRepos: Entity[],
  batchResponse: WatchtowerBatchResponse,
  userSupIds: string[],
): Entity[] {
  return catalogRepos.filter(repo => {
    const githubProjectSlug =
      repo.metadata.annotations?.['github.com/project-slug'];

    if (!githubProjectSlug) {
      return true; // No slug, can't validate - include by default
    }

    const repoName = githubProjectSlug.split('/')[1] || githubProjectSlug;
    const wtRevision = batchResponse.data[repoName];

    if (!wtRevision) {
      return true; // No watchtower revision, still belongs to catalog's team
    }

    // Use recommended_team_id for all recommendation types
    if (!wtRevision.recommended_team_id) {
      return true; // No target team specified, include by default
    }

    // Watchtower has a revision - check if it still belongs to user's teams
    return userSupIds.includes(wtRevision.recommended_team_id.toUpperCase());
  });
}

/**
 * Refresh repository entities via utility API
 */
export async function refreshRepositories(
  discoveryApi: DiscoveryApi,
  repositoryNames: string[],
): Promise<{ updatedCount: number }> {
  const baseUrl = await discoveryApi.getBaseUrl('utility-api');

  const response = await fetch(`${baseUrl}/actions/refresh-repo-ownership`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ repository_names: repositoryNames }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to refresh repositories: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}
